import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, 
  X, 
  Send, 
  Search, 
  Home, 
  ChevronRight, 
  Terminal,
  Cpu,
  Zap,
  Globe
} from 'lucide-react';

const QuantumAIBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: 'Hello! I\'m your Quantum Navigator. I can help you explore Godquantum Systems\' quantum computing platform. Try asking about our products, technology, or use cases!',
      timestamp: new Date()
    }
  ]);
  const [currentPath, setCurrentPath] = useState('/');
  const messagesEndRef = useRef(null);

  // Navigation structure in Linux-style directory format
  const navigationTree = {
    '/': ['Company/', 'Product/', 'Platform/', 'Technology/', 'Partners/', 'Support/'],
    '/Company/': ['About Us', 'Vision', 'Why Godquantum?', 'Careers', 'Newsletter', 'Investors', 'Contact Us'],
    '/Product/': ['Software/', 'Hardware/'],
    '/Product/Software/': ['Control System', 'GUI', 'APIs'],
    '/Product/Hardware/': ['QubitPulsing™', 'QubitFabrica™'],
    '/Platform/': ['QubitHub™', 'QubitCali™', 'QubitDeviG™', 'QubitEngine™', 'QubitTrans™'],
    '/Technology/': ['Applications', 'Use Cases'],
    '/Partners/': ['Current Partners', 'Partner Program'],
    '/Support/': ['FAQs', 'Community Discord', 'Documentation']
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (!message.trim()) return;

    const newMessage = {
      id: messages.length + 1,
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);

    // Simple AI response simulation
    setTimeout(() => {
      const botResponse = generateBotResponse(message);
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        type: 'bot',
        content: botResponse,
        timestamp: new Date()
      }]);
    }, 1000);

    setMessage('');
  };

  const generateBotResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();
    
    if (msg.includes('quantum') || msg.includes('computing')) {
      return "Our Quantum OS provides fault-tolerant quantum computing with unified platform engineering. We offer full-stack solutions including hardware (QubitPulsing™, QubitFabrica™) and software (Control Systems, APIs, GUI).";
    }
    
    if (msg.includes('product') || msg.includes('platform')) {
      return "Our platform includes:\n• QubitHub™ - Hyperscaler Global DCaaS\n• QubitEngine™ - Core Quantum Engine\n• QubitCali™ - Superconducting Qubit Calibration\n• QubitDeviG™ - Device Gateway\n• QubitTrans™ - Polymath LangChain Framework";
    }
    
    if (msg.includes('application') || msg.includes('use case')) {
      return "Quantum computing applications span AI/ML, Robotics, Drug Discovery, Financial Optimization, Urban Planning, Agriculture Sciences, and Meteorological Forecasting. Would you like to explore specific industry solutions?";
    }
    
    if (msg.includes('team') || msg.includes('about')) {
      return "We're a team of passionate system architects, software engineers, theoretical physicists, quantum physicists, power electronics and chip designers working on advanced deep tech quantum computing solutions.";
    }

    return "I can help you navigate our quantum computing platform. Try asking about our products, technology stack, applications, or team. You can also use commands like 'ls /' to explore our directory structure!";
  };

  const handleDirectoryClick = (path) => {
    setCurrentPath(path);
  };

  const renderDirectoryTree = () => {
    const currentItems = navigationTree[currentPath] || [];
    
    return (
      <div className="bg-black/80 rounded-lg p-4 font-mono text-sm border border-emerald-500/30">
        <div className="text-emerald-400 mb-2">
          <Terminal className="inline w-4 h-4 mr-2" />
          quantum@godquantum:{currentPath}$
        </div>
        <div className="space-y-1">
          {currentPath !== '/' && (
            <div 
              className="flex items-center text-gray-400 hover:text-emerald-300 cursor-pointer"
              onClick={() => handleDirectoryClick('/')}
            >
              <ChevronRight className="w-3 h-3 mr-1" />
              ../
            </div>
          )}
          {currentItems.map((item, index) => (
            <div 
              key={index}
              className={`flex items-center cursor-pointer hover:text-emerald-300 transition-colors ${
                item.endsWith('/') ? 'text-cyan-400' : 'text-gray-300'
              }`}
              onClick={() => item.endsWith('/') && handleDirectoryClick(currentPath + item)}
            >
              <ChevronRight className="w-3 h-3 mr-1" />
              {item}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Bot Toggle Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full shadow-xl flex items-center justify-center text-white hover:shadow-2xl transition-all duration-300"
          aria-label="Open Quantum AI Assistant"
        >
          {isOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <div className="relative">
              <MessageCircle className="w-6 h-6" />
              {/* Quantum-inspired pulsing effect */}
              <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-30"></div>
            </div>
          )}
        </button>
      </motion.div>

      {/* Bot Chat Interface */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 w-96 h-[600px] bg-black/95 backdrop-blur border border-emerald-500/30 rounded-2xl shadow-2xl z-40 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-8 h-8 bg-black/20 rounded-lg flex items-center justify-center">
                    <Cpu className="w-4 h-4 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Quantum Navigator</h3>
                  <p className="text-emerald-100 text-xs">AI-Powered Quantum Guide</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <Zap className="w-3 h-3 text-yellow-300 animate-pulse" />
                  <Globe className="w-3 h-3 text-blue-300 animate-spin" />
                  <div className="w-3 h-3 bg-emerald-300 rounded-full animate-bounce"></div>
                </div>
              </div>
            </div>

            {/* Navigation Tree */}
            <div className="p-4 border-b border-emerald-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-mono">Navigation Tree</span>
              </div>
              {renderDirectoryTree()}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.type === 'user'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-800 text-gray-100 border border-emerald-500/20'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <span className="text-xs opacity-70 mt-1 block">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-emerald-500/20">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Ask about quantum computing..."
                  className="flex-1 bg-gray-900 border border-emerald-500/30 rounded-xl px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400 text-sm"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-700 text-white p-2 rounded-xl transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuantumAIBot;